import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import { Zap, Flame } from "lucide-react";
import { useSite } from "@/lib/lang";
import { ui } from "@/lib/uiStrings";
import { api } from "@/lib/api";
import Nav from "@/components/site/Nav";
import { Reveal } from "@/components/site/Reveal";
import { fmtPrice, fmtDate, ChangeChip, PriceTooltip } from "@/components/site/Osservatorio";

const PERIODS = [
  { days: 30, key: "giorni30" },
  { days: 90, key: "mesi3" },
  { days: 180, key: "mesi6" },
  { days: 365, key: "mesi12" },
];

function periodStats(series) {
  const vals = series.map((d) => d.value).filter((v) => v != null);
  if (!vals.length) return null;
  const last = vals[vals.length - 1];
  const first = vals[0];
  return {
    current: last,
    avg: vals.reduce((a, b) => a + b, 0) / vals.length,
    min: Math.min(...vals),
    max: Math.max(...vals),
    change_pct: first ? ((last - first) / first) * 100 : null,
  };
}

function MetricSection({ id, icon: Icon, title, sub, series, lang, t, testId }) {
  const stats = useMemo(() => periodStats(series), [series]);
  return (
    <section id={id} data-testid={testId} className="py-12 lg:py-16 border-t border-ice/8 first:border-t-0 first:pt-0">
      <Reveal>
        <div className="flex items-center gap-3.5">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-royal/10 border border-royal/20 text-royal shrink-0">
            <Icon size={20} />
          </span>
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ice">{title}</h2>
            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground mt-1">{sub}</p>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid={`${testId}-stats`}>
          <div className="bg-surface border border-royal/30 rounded-xl p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-azure">{t.valoreAttuale}</p>
            <p className="mt-2 font-display text-xl sm:text-2xl font-bold text-ice">{fmtPrice(stats?.current)}</p>
            <p className="text-xs text-muted-foreground">€/MWh</p>
          </div>
          <div className="bg-surface border border-ice/8 rounded-xl p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t.mediaPeriodo}</p>
            <p className="mt-2 font-display text-xl sm:text-2xl font-bold text-ice">{fmtPrice(stats?.avg)}</p>
            <p className="text-xs text-muted-foreground">€/MWh</p>
          </div>
          <div className="bg-surface border border-ice/8 rounded-xl p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t.minimo}</p>
            <p className="mt-2 font-display text-xl sm:text-2xl font-bold text-ice">{fmtPrice(stats?.min)}</p>
            <p className="text-xs text-muted-foreground">€/MWh</p>
          </div>
          <div className="bg-surface border border-ice/8 rounded-xl p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t.massimo}</p>
            <p className="mt-2 font-display text-xl sm:text-2xl font-bold text-ice">{fmtPrice(stats?.max)}</p>
            <p className="text-xs text-muted-foreground">€/MWh</p>
          </div>
          <div className="bg-surface border border-ice/8 rounded-xl p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t.variazione}</p>
            <div className="mt-2"><ChangeChip value={stats?.change_pct} testId={`${testId}-change`} /></div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-8 bg-surface border border-ice/8 rounded-2xl p-5 sm:p-7">
          <div className="h-[300px] sm:h-[360px]" data-testid={`${testId}-chart`}>
            <ResponsiveContainer width="100%" height="100%" debounce={80}>
              <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="rgba(14,27,51,0.06)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={fmtDate}
                  tick={{ fill: "#8FA3C0", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={40}
                />
                <YAxis
                  tick={{ fill: "#8FA3C0", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={64}
                  domain={["auto", "auto"]}
                  tickFormatter={(v) => fmtPrice(v)}
                />
                <RTooltip content={<PriceTooltip />} cursor={{ stroke: "rgba(58,91,217,0.25)" }} />
                <Line type="monotone" dataKey="value" stroke="#3A5BD9" strokeWidth={2.2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export default function OsservatorioPage() {
  const { lang } = useSite();
  const t = ui[lang].osservatorio;
  const [days, setDays] = useState(30);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    try {
      if (window.location.hash) {
        const el = document.querySelector(window.location.hash);
        if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
      }
    } catch {
      /* hash non valido: ignora */
    }
  }, []);

  useEffect(() => {
    api.get("/energy-prices/history", { params: { days } }).then((r) => setHistory(r.data)).catch(() => {});
  }, [days]);

  const punSeries = history.filter((d) => d.pun_eur_mwh != null).map((d) => ({ date: d.date, value: d.pun_eur_mwh }));
  const psvSeries = history.filter((d) => d.psv_eur_mwh != null).map((d) => ({ date: d.date, value: d.psv_eur_mwh }));

  return (
    <div className="min-h-screen bg-onyx text-ice">
      <Nav />
      <main className="mx-auto max-w-7xl px-6 pt-28 lg:pt-36 pb-16" data-testid="osservatorio-page">
        <Reveal>
          <p className="font-mono text-xs font-semibold tracking-[0.2em] text-royal uppercase">{t.label}</p>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight max-w-3xl">
            {t.heading}
          </h1>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl">{t.sub}</p>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="oss-period-selector">
            {PERIODS.map((p) => (
              <button
                key={p.days}
                data-testid={`oss-period-${p.days}`}
                onClick={() => setDays(p.days)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  days === p.days ? "bg-royal text-white" : "text-muted-foreground hover:text-ice border border-ice/10"
                }`}
              >
                {t[p.key]}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10">
          <MetricSection id="pun" icon={Zap} title={t.punTitle} sub={t.punSub} series={punSeries} lang={lang} t={t} testId="oss-page-pun" />
          <MetricSection id="psv" icon={Flame} title={t.psvTitle} sub={t.psvSub} series={psvSeries} lang={lang} t={t} testId="oss-page-psv" />
        </div>

        <p className="mt-10 font-mono text-[11px] text-muted-foreground/70">{t.fonte}</p>
        <Link to="/" className="mt-6 inline-block text-azure hover:underline text-sm">
          ← {lang === "it" ? "Torna alla home" : "Back to home"}
        </Link>
      </main>

      <footer className="border-t border-ice/5">
        <div className="mx-auto max-w-7xl px-6 py-6">
          <p className="font-mono text-xs text-muted-foreground/60">
            © {new Date().getFullYear()} RR Solutions srl — ETHIC ENERGY · P.IVA 02582720997
          </p>
        </div>
      </footer>
    </div>
  );
}
